import React, { useState, useEffect } from "react";
import axios from "axios";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import styles from "./adminStatistics.module.css";
import { FaUser } from "react-icons/fa";
import { IoDocumentTextOutline } from "react-icons/io5";
import { FaGraduationCap } from "react-icons/fa6";
import { MdOnlinePrediction } from "react-icons/md";
import { FaUserFriends } from "react-icons/fa";
import { FaArrowRight } from "react-icons/fa";
import PieChart from "../../components/pieChart/PieChart";
import BarChart from "../../components/barChart/BarChart";
import defaultUserImg from "../../images/defaultUserImg.png";

const apiUrl = process.env.REACT_APP_URL;

const AdminStatistics = () => {
  /**
   * This component is responsible for displaying information and statistics about the website activity to the administrator. The information includes: the total number of users on the website, the number of new users during the last month, the number of new posts during the last month, the number of learning zones, the number of online users, and basic information about the newest user. Also, information about the learning zones distribution by language is provided, as well as monthly data about the number of new posts and the number of new users during the last year. There is also an option to choose other dates (instead of the default last year) for the last two statistics.
   */

  const [usersData, setUsersData] = useState({});
  const [postsData, setPostsData] = useState([]);
  const [learningZonesData, setLearningZonesData] = useState([]);

  const [startDate, setStartDate] = useState(
    new Date(new Date().setMonth(new Date().getMonth() - 12))
  );
  const [endDate, setEndDate] = useState(new Date());

  /**
   * Create and return a list of monthly labels for a given period of time.
   * @param {*} start the first date of the period
   * @param {*} end the last date of the period
   * @returns an array of labels - one for each month within the period
   */
  const generateMonthLabels = (start, end) => {
    const labels = [];
    const date = new Date(start);

    while (date <= end) {
      labels.push(
        `${date.toLocaleString("default", {
          month: "long",
        })} ${date.getFullYear()}`
      );
      date.setMonth(date.getMonth() + 1);
    }

    return labels;
  };

  /**
   * Given monthly data in a period of time, add values for months without values. This is used for adding a value of 0 to months within the period that do not have a value in the passed data argument (the data received from the backend may exclude months with value 0)
   * @param {*} data - an array of objects - the data to manipulate
   * @param {*} start - the first date of the period
   * @param {*} end - the last date of the period
   * @returns the data array, with values for all months.
   */
  const fillMissingMonths = (data, start, end) => {
    const monthLabels = generateMonthLabels(start, end);

    const filledData = monthLabels.map((label) => {
      const existingData = data.find((item) => item.month === label);

      return existingData
        ? { label, value: existingData.count }
        : { label, value: 0 };
    });

    return filledData;
  };

  /**
   * Get website data from the database: users data, posts data, and learning zones data.
   * @param {*} start - the first date of the period to get the data for
   * @param {*} end - the last date of the period to get the data for
   */
  const fetchData = async (start, end) => {
    const res = await axios.get(`${apiUrl}/admin/statistics`, {
      withCredentials: true,
      params: {
        startDate: start.toISOString().split("T")[0],
        endDate: end.toISOString().split("T")[0],
      },
    });

    if (res.data && res.data.success) {
      const statistics = res.data.statistics;
      setUsersData(statistics.usersData || {});
      setPostsData(statistics.postsData || []);
      setLearningZonesData(statistics.learningZonesData || []);
    }
  };

  /**
   * Open the profile page of a given user, in a new tab.
   * @param {*} username - the username of the user to open the profile page of
   */
  const visitProfile = (username) => {
    window.open(`/profile/${username}`, "_blank", "noopener,noreferrer");
  };

  // Get the statistics from the database
  useEffect(() => {
    fetchData(startDate, endDate);
  }, [startDate, endDate]);

  const newPostsPerMonth = fillMissingMonths(
    postsData.monthlyPosts || [],
    startDate,
    endDate
  );

  const newUsersPerMonth = fillMissingMonths(
    usersData.monthlyReg || [],
    startDate,
    endDate
  );

  /**
   * Process the learning zones data - sort the learning zones languages by popularity (percentage) and ensure the number of language groups is 6 (if there are more, an "other" group will be created and contain the smallest groups).
   * @param {*} data - array of objects - the learning zones language statistics
   */
  const processLearningZonesData = (data) => {
    const maxGroups = 6;
    let processedData = [...data];

    if (processedData.length > maxGroups) {
      processedData.sort(
        (a, b) =>
          parseFloat(a.language_percentage) - parseFloat(b.language_percentage)
      );

      let otherTotal = 0;
      for (let i = 0; i < processedData.length - (maxGroups - 1); i++) {
        otherTotal += parseFloat(processedData[i].language_percentage);
      }

      processedData = [
        ...processedData.slice(processedData.length - (maxGroups - 1)),
        { language: "Other", language_percentage: otherTotal.toFixed(1) },
      ];
    }

    return processedData.map((language) => ({
      label: language.language,
      value: parseFloat(language.language_percentage),
    }));
  };

  const learningZonesDist = processLearningZonesData(learningZonesData);

  const learningZonesDesc = learningZonesDist
    .slice()
    .reverse()
    .map((language, idx) => (
      <p key={idx}>
        {language.label} - {language.value}%
      </p>
    ));

  const screenWidth = window.innerWidth;
  const showLZDesc = screenWidth < 1000;

  return (
    <div className={styles.adminStatistics}>
      <div className={styles.websiteData}>
        <table className={styles.websiteTable}>
          <thead>
            <tr>
              <th>Category</th>
              <th>Count</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>
                Total number of users
                <span className={styles.dataIcon}>
                  <FaUser />
                </span>
              </td>
              <td>
                {usersData.totalUsers && usersData.totalUsers.length > 0
                  ? usersData.totalUsers[0].total_users
                  : "No data available"}
              </td>
            </tr>
            <tr>
              <td>
                New users during the last month
                <span className={styles.dataIcon}>
                  <FaUserFriends />
                </span>
              </td>
              <td>
                {usersData.lastMonthUsers && usersData.lastMonthUsers.length > 0
                  ? usersData.lastMonthUsers[0].new_users_last_month
                  : "No data available"}
              </td>
            </tr>
            <tr>
              <td>
                New posts during the last month
                <span className={styles.dataIcon}>
                  <IoDocumentTextOutline />
                </span>
              </td>
              <td>
                {postsData.lastMonthPosts && postsData.lastMonthPosts.length > 0
                  ? postsData.lastMonthPosts[0].new_posts_last_month
                  : "No data available"}
              </td>
            </tr>
            <tr>
              <td>
                Number of learning zones
                <span className={styles.dataIcon}>
                  <FaGraduationCap />
                </span>
              </td>
              <td>
                {learningZonesData && learningZonesData.length > 0
                  ? learningZonesData[0].total_learning_zones
                  : "No data available"}
              </td>
            </tr>
            <tr>
              <td>
                Number of online users
                <span className={styles.dataIcon}>
                  <MdOnlinePrediction />
                </span>
              </td>
              <td>
                {usersData.onlineUsers && usersData.onlineUsers.length > 0
                  ? usersData.onlineUsers[0].online_users
                  : "No data available"}
              </td>
            </tr>
          </tbody>
        </table>

        <div className={styles.newUser}>
          <div>
            <span className={styles.newUserArrow}>
              <FaArrowRight />
            </span>
            <span className={styles.newUserLabel}>Newest user</span>
          </div>

          <div
            className={styles.newUserDetails}
            onClick={() => visitProfile(usersData.newestUser[0].username)}
          >
            <p className={styles.newUserUsername}>
              {usersData.newestUser && usersData.newestUser.length > 0
                ? usersData.newestUser[0].username
                : "No data available"}
            </p>
            <img
              src={
                usersData.newestUser && usersData.newestUser.length > 0
                  ? usersData.newestUser[0].img_link || defaultUserImg
                  : defaultUserImg
              }
              alt={
                usersData.newestUser && usersData.newestUser.length > 0
                  ? usersData.newestUser[0].username
                  : "Newest User"
              }
              className={styles.newUserImg}
            />
          </div>
          <p className={styles.newUserRegistrationDate}>
            (
            {usersData.newestUser && usersData.newestUser.length > 0
              ? usersData.newestUser[0].reg_date
              : "No data available"}
            )
          </p>
        </div>
      </div>

      <div className={styles.websiteGraphs}>
        <div className={styles.pieGraph}>
          <h2>Learning Zones Distribution</h2>
          {showLZDesc && (
            <div className={styles.lzDesc}>{learningZonesDesc}</div>
          )}
          {!showLZDesc && <PieChart data={learningZonesDist} />}
        </div>

        <h2 className={styles.growthHeader}>Website Monthly Growth</h2>

        <div className={styles.datePickers}>
          <div className={styles.datePicker}>
            <label>Start Date:</label>
            <DatePicker
              selected={startDate}
              onChange={(date) => setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={new Date()}
              dateFormat="yyyy-MM-dd"
            />
          </div>
          <div className={styles.datePicker}>
            <label>End Date:</label>
            <DatePicker
              selected={endDate}
              onChange={(date) => setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              maxDate={new Date()}
              dateFormat="yyyy-MM-dd"
            />
          </div>
        </div>

        <div className={styles.graph}>
          <h2>New Posts Per Month</h2>
          <BarChart data={newPostsPerMonth} />
        </div>

        <div className={styles.graph}>
          <h2>New Users Per Month</h2>
          <BarChart data={newUsersPerMonth} chartLabel="New Users" />
        </div>
      </div>
    </div>
  );
};

export default AdminStatistics;
